import { useTranslation } from "react-i18next";
import { contactInfo } from "@/mocks/homeData";

interface HeroContactanosProps {
  onOpenQuote?: (service?: string) => void;
}

export default function HeroContactanos({ onOpenQuote }: HeroContactanosProps) {
  const { t } = useTranslation("common");

  const scrollToForm = () => {
    const el = document.querySelector("#contact");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  const chips = [
    { icon: "ri-whatsapp-line", label: t("contact.whatsapp"), value: contactInfo.phone, href: contactInfo.whatsappHref },
    { icon: "ri-mail-line", label: t("contact.email"), value: contactInfo.email },
    { icon: "ri-time-line", label: t("contact.availability"), value: t("contact.availabilityValue") },
    { icon: "ri-global-line", label: t("contact.coverage"), value: t("contact.coverageValue") },
  ];

  return (
    <section id="hero-contact" className="relative w-full h-[580px] md:h-[660px] overflow-hidden">
      {/* Background */}
      <img
        src="https://readdy.ai/api/search-image?query=Friendly%20customer%20support%20team%20working%20together%20in%20a%20bright%20modern%20office%20with%20large%20windows%20and%20green%20plants%2C%20warm%20natural%20daylight%2C%20welcoming%20professional%20atmosphere%2C%20diverse%20specialists%20collaborating%2C%20editorial%20corporate%20photography%2C%20human%20centered%20composition%2C%20soft%20depth%20of%20field&width=1600&height=800&seq=contact-hero-01&orientation=landscape"
        alt={t("contactPage.title")}
        className="absolute inset-0 w-full h-full object-cover object-center"
        decoding="async"
        fetchPriority="high"
      />

      {/* Overlays (homologated contrast) */}
      <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/70 to-black/45" />
      <div className="absolute inset-0 bg-gradient-to-t from-background-950/95 via-transparent to-black/55" />
      <div className="absolute inset-0 bg-black/45 md:hidden" />

      {/* Bottom transition */}
      <div className="absolute bottom-0 left-0 right-0 h-28 md:h-40 bg-gradient-to-b from-transparent to-background-950 pointer-events-none" />

      {/* Tech grid */}
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.03]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(34,211,238,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(34,211,238,0.5) 1px, transparent 1px)",
          backgroundSize: "60px 60px",
        }}
      />

      {/* Content */}
      <div className="absolute inset-0 flex items-center z-20">
        <div className="w-full px-5 sm:px-10 lg:px-16 pt-16 sm:pt-20">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-primary-400/30 bg-primary-500/10 mb-5">
              <span className="w-1.5 h-1.5 rounded-full bg-primary-400 animate-pulse" />
              <span className="text-[10px] font-label font-semibold text-primary-300 uppercase tracking-[0.2em]">
                {t("contactPage.tag")}
              </span>
            </div>

            <h1 className="font-heading font-bold text-[26px] sm:text-4xl md:text-5xl lg:text-[3.4rem] text-white leading-[1.12] mb-4">
              {t("contactPage.title")}{" "}
              <span className="bg-gradient-to-r from-primary-300 via-primary-400 to-cyan-400 bg-clip-text text-transparent">
                {t("contactPage.highlight")}
              </span>
            </h1>

            <p className="text-sm md:text-base text-white/75 leading-relaxed mb-7 max-w-xl">
              {t("contactPage.subtitle")}
            </p>

            {/* Quick contact chips */}
            <div className="flex flex-wrap gap-3 mb-8 max-w-2xl">
              {chips.map((chip) => (
                <div
                  key={chip.label}
                  className="flex items-center gap-3 px-4 py-2.5 rounded-xl border border-white/10 bg-white/5 backdrop-blur-md min-w-0"
                >
                  <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-500/15 text-primary-300 flex-shrink-0">
                    <i className={`${chip.icon} text-base`} />
                  </div>
                  <div className="min-w-0">
                    <p className="text-[10px] font-label font-semibold text-foreground-400/60 uppercase tracking-wider">
                      {chip.label}
                    </p>
                    {chip.href ? (
                      <a
                        href={chip.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-foreground-100 truncate hover:text-[#25D366] transition-colors cursor-pointer"
                      >
                        {chip.value}
                      </a>
                    ) : (
                      <p className="text-sm text-foreground-100 truncate">{chip.value}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <a
                href="#contact"
                onClick={(e) => {
                  e.preventDefault();
                  scrollToForm();
                }}
                className="group inline-flex w-full sm:w-auto items-center justify-between sm:justify-start gap-4 pl-6 pr-2 py-2 rounded-full border border-primary-400/50 bg-primary-500/15 backdrop-blur-md text-white font-label font-medium text-sm md:text-base hover:border-primary-400/80 hover:bg-primary-500/25 transition-all duration-500 whitespace-nowrap cursor-pointer"
              >
                <span>{t("contactPage.scrollForm")}</span>
                <span className="flex items-center justify-center w-9 h-9 md:w-10 md:h-10 rounded-full bg-primary-500 text-white group-hover:bg-primary-400 transition-colors duration-300">
                  <i className="ri-arrow-down-line text-base md:text-lg group-hover:translate-y-0.5 transition-transform duration-300" />
                </span>
              </a>
              <button
                type="button"
                onClick={() => onOpenQuote?.("Contacto")}
                className="group inline-flex w-full sm:w-auto items-center justify-between sm:justify-start gap-4 pl-6 pr-2 py-2 rounded-full border border-white/15 bg-white/5 backdrop-blur-md text-foreground-100 font-label font-medium text-sm md:text-base hover:border-primary-400/40 hover:bg-white/10 transition-all duration-500 whitespace-nowrap cursor-pointer"
              >
                <span>{t("shared.ctaSecondary")}</span>
                <span className="flex items-center justify-center w-9 h-9 md:w-10 md:h-10 rounded-full border border-primary-400/40 text-primary-300 group-hover:bg-primary-500 group-hover:text-white group-hover:border-primary-500 transition-all duration-300">
                  <i className="ri-chat-smile-3-line text-base md:text-lg" />
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}